import { useEffect, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle } from "lucide-react";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { CatalogueMigrationRowListOptions } from "@shared-core/data/adapter";
import type {
  CatalogueMigration,
  CatalogueMigrationReviewRow,
  CatalogueMigrationStatus,
} from "@shared-core/data/api-types";
import { ApiError } from "@shared-core/data/api/errors";
import {
  REVIEW_STATUS_FILTERS,
  REVIEW_STATUS_LABELS,
} from "@shared-core/domain/catalogue-migration";
import { formatCount, formatDateOrDash } from "@shared-core/domain/formatters";
import { useDebouncedValue } from "@shared-core/domain/useDebouncedValue";
import { paths } from "@shared-core/routing/routes";
import { Badge } from "../../app/components/primitives/badge";
import { Label } from "../../app/components/primitives/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../app/components/primitives/table";
import { ListPagination } from "../../app/components/patterns/ListPagination";
import { selectClassName } from "../stock/StockFormField";

// Server-side filter, search, sort and paging (docs/list-endpoints.md).

const PAGE_SIZE = 25;

const SORT_OPTIONS = [
  { value: "order", label: "Decisions first" },
  { value: "title", label: "Name" },
  { value: "-last_ordered", label: "Last ordered" },
  { value: "-jobs", label: "Most jobs" },
  { value: "-held", label: "Most held" },
];

/**
 * The migration's review list (spec 5): one row per piece of work and what
 * becomes of it, as the customer's committed migration file recorded it. Read
 * only: a decision is recorded in the file with Claude and applied at the next
 * deploy.
 */
export function ReviewListTab({
  migration,
}: {
  migration: CatalogueMigration;
}) {
  const dataAdapter = useDataAdapter();
  const [rows, setRows] = useState<CatalogueMigrationReviewRow[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [firstLoad, setFirstLoad] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [status, setStatus] = useState<CatalogueMigrationStatus | "">("");
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 300);
  const [sort, setSort] = useState(SORT_OPTIONS[0]!.value);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
  }, [status, debouncedSearch, sort]);

  useEffect(() => {
    if (!migration.has_migration) return;
    let cancelled = false;
    const options: CatalogueMigrationRowListOptions = {
      page,
      pageSize: PAGE_SIZE,
      sort,
    };
    if (debouncedSearch.trim()) options.q = debouncedSearch.trim();
    if (status) options.status = status;
    setLoadError(false);
    dataAdapter
      .listCatalogueMigrationRows(migration.customer_id, options)
      .then((result) => {
        if (cancelled) return;
        setRows(result.items);
        setTotalPages(result.totalPages);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        console.error("catalogue_migration.rows.load_failed", {
          status: error instanceof ApiError ? error.status : undefined,
        });
        setLoadError(true);
      })
      .finally(() => {
        if (!cancelled) setFirstLoad(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    dataAdapter,
    migration.customer_id,
    migration.has_migration,
    page,
    debouncedSearch,
    status,
    sort,
  ]);

  if (!migration.has_migration) {
    return (
      <div className="text-sm p-6 rounded-lg border border-dashed border-border text-muted-foreground space-y-1">
        <p className="font-medium text-foreground">No migration file yet</p>
        <p>
          Ask Claude to run the migrate-customer-catalogue skill for{" "}
          {migration.customer_name ?? "this customer"}. The review list appears
          here once its file has been applied at a deploy.
        </p>
      </div>
    );
  }

  const counts = migration.status_counts;
  const chips: {
    value: CatalogueMigrationStatus | "";
    label: string;
    count: number;
  }[] = [
    { value: "", label: "All", count: counts.all },
    ...REVIEW_STATUS_FILTERS.map((s) => ({
      value: s,
      label: REVIEW_STATUS_LABELS[s],
      count: counts[s],
    })).filter(
      (chip) =>
        chip.count > 0 ||
        (chip.value !== "stays_on_tharstern" && chip.value !== "brandhub"),
    ),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-4">
        <div
          role="group"
          aria-label="Status"
          className="flex flex-wrap items-center gap-2"
        >
          {chips.map((chip) => (
            <button
              key={chip.value || "all"}
              type="button"
              aria-pressed={status === chip.value}
              onClick={() => setStatus(chip.value)}
              className={`px-3 py-1 rounded-full border text-sm transition-colors ${
                status === chip.value
                  ? "bg-primary/10 border-primary text-primary font-medium"
                  : "border-border text-muted-foreground hover:text-foreground"
              }`}
            >
              {chip.label}
              <span className="ml-1.5 tabular-nums">
                {formatCount(chip.count)}
              </span>
            </button>
          ))}
        </div>
        <div className="space-y-1 w-full sm:w-56 sm:ml-auto">
          <Label htmlFor="review-search">Work</Label>
          <input
            id="review-search"
            type="search"
            placeholder="Name, stock code or ref…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="px-3 py-2 border border-border rounded bg-background text-sm w-full"
          />
        </div>
        <div className="space-y-1">
          <Label htmlFor="review-sort">Sort by</Label>
          <select
            id="review-sort"
            className={`${selectClassName} w-auto`}
            value={sort}
            onChange={(e) => setSort(e.target.value)}
          >
            {SORT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <p className="text-sm text-muted-foreground">
        Decisions are recorded in the migration file with Claude (the
        migrate-customer-catalogue skill), then applied at the next deploy.
      </p>

      {loadError ? (
        <div
          role="alert"
          className="flex items-center gap-2 text-sm p-4 border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20 rounded-lg text-red-700 dark:text-red-300"
        >
          <AlertTriangle className="size-4 shrink-0" />
          Couldn't load the review list. Please try again.
        </div>
      ) : firstLoad ? (
        <div className="flex items-center justify-center min-h-[160px]">
          <div className="text-muted-foreground">Loading the review list…</div>
        </div>
      ) : rows.length === 0 ? (
        <div className="text-muted-foreground text-sm p-8 text-center border border-border rounded-lg">
          Nothing matches those filters.
        </div>
      ) : status === "needs_decision" ? (
        <NeedsDecisionPanel rows={rows} />
      ) : status === "left_for_martin" ? (
        <LeftForMartinPanel rows={rows} />
      ) : (
        <WorkTable rows={rows} />
      )}
      <ListPagination
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
      />
    </div>
  );
}

function WorkTable({ rows }: { rows: CatalogueMigrationReviewRow[] }) {
  return (
    <div className="rounded-lg border border-border bg-surface overflow-x-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Work</TableHead>
            <TableHead>Why it is here</TableHead>
            <TableHead className="text-right">Jobs</TableHead>
            <TableHead>Last ordered</TableHead>
            <TableHead>Becomes</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.key}>
              <TableCell>
                <div className="font-medium">{row.title}</div>
                <div className="text-xs text-muted-foreground">
                  {[
                    row.stock_code && `stock ${row.stock_code}`,
                    row.customer_product_ref &&
                      `product code ${row.customer_product_ref}`,
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </TableCell>
              <TableCell className="text-sm">{row.why}</TableCell>
              <TableCell className="text-right tabular-nums">
                {row.jobs > 0 ? formatCount(row.jobs) : "—"}
              </TableCell>
              <TableCell className="whitespace-nowrap">
                {row.last_ordered ? formatDateOrDash(row.last_ordered) : "—"}
              </TableCell>
              <TableCell>
                <Becomes row={row} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function Becomes({ row }: { row: CatalogueMigrationReviewRow }) {
  switch (row.status) {
    case "becomes_item":
      return (
        <div className="space-y-0.5 text-sm">
          <div className="flex flex-wrap items-center gap-1.5">
            <Badge variant="success">Item</Badge>
            {row.catalogue_item_id && row.is_item_applied ? (
              <Link
                to={paths.catalogueItem({ itemId: row.catalogue_item_id })}
                className="underline"
              >
                {row.becomes_name ?? row.title}
              </Link>
            ) : (
              <span>{row.becomes_name ?? row.title}</span>
            )}
          </div>
          {row.route_hint && (
            <div className="text-xs text-muted-foreground">
              Route hint: {row.route_hint}
            </div>
          )}
          {!row.is_item_applied && (
            <div className="text-xs text-muted-foreground">
              Applied at the next deploy
            </div>
          )}
        </div>
      );
    case "brandhub":
      return (
        <div className="space-y-0.5 text-sm">
          <div>{REVIEW_STATUS_LABELS.brandhub}</div>
          <div className="text-xs text-muted-foreground">
            Decided in the BrandHub mapping
          </div>
        </div>
      );
    default:
      return (
        <div className="space-y-0.5 text-sm">
          <Badge
            variant={
              row.status === "needs_decision" ||
              row.status === "left_for_martin"
                ? "warning"
                : "neutral"
            }
          >
            {REVIEW_STATUS_LABELS[row.status]}
          </Badge>
          {row.reason && (
            <div className="text-xs text-muted-foreground">{row.reason}</div>
          )}
        </div>
      );
  }
}

function NeedsDecisionPanel({ rows }: { rows: CatalogueMigrationReviewRow[] }) {
  return (
    <section aria-label="Needs a decision" className="space-y-3">
      <h2 className="text-lg">Needs a decision: {formatCount(rows.length)}</h2>
      <ul className="space-y-3">
        {rows.map((row) => (
          <li
            key={row.key}
            className="rounded-lg border border-border bg-surface p-4 space-y-1"
          >
            <div className="font-medium">{row.title}</div>
            <div className="text-sm text-muted-foreground">{row.why}</div>
            {row.question && (
              <div className="text-sm">
                {row.question.suggestion && <p>{row.question.suggestion}</p>}
                <p className="text-muted-foreground">
                  Decides: {row.question.decider}
                </p>
              </div>
            )}
            <Badge variant="warning">
              {row.question?.decider.includes("Chris")
                ? "Waiting on Chris"
                : "Open"}
            </Badge>
          </li>
        ))}
      </ul>
    </section>
  );
}

function LeftForMartinPanel({ rows }: { rows: CatalogueMigrationReviewRow[] }) {
  return (
    <section aria-label="Left for Martin" className="space-y-3">
      <h2 className="text-lg">Left for Martin: {formatCount(rows.length)}</h2>
      <p className="text-sm text-muted-foreground">
        No template estimate, and the description does not say enough. They cut
        over unlinked: held and called off, not reprinted.
      </p>
      <div className="rounded-lg border border-border bg-surface overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Stock product</TableHead>
              <TableHead>Description</TableHead>
              <TableHead>What the parse read</TableHead>
              <TableHead className="text-right">Held</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="font-medium">{row.stock_code}</TableCell>
                <TableCell>{row.title}</TableCell>
                <TableCell>{row.reading ?? "—"}</TableCell>
                <TableCell className="text-right tabular-nums">
                  {row.held == null ? "—" : formatCount(row.held)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}
